import React from 'react';
import pick from 'lodash/pick';
import {
    WHITELISTED_EVENT_PARAMETERS,
    ERROR_MESSAGE_PROPS,
} from '../constants';
import { pricingCalculatorApi } from '../api';
import { formatAPIResponse, formatDataForAPI } from '../utils';
import { validate } from '../validations';
import { formatNumber, unformat, getCurrencyPrecision } from 'js-utils/intl';

export class PricingCalculator extends React.Component {
    static defaultProps = {
        ticketPrice: '',
        ticketQuantity: '',
        assortment: 'package1',
        passOnFees: true,
        eventTotal: '',
        attendeeTotal: '',
        currencySymbol: '',
        isLoading: false,
        isFree: true,
    };

    initialState = {
        ticketPrice: this.props.ticketPrice,
        ticketQuantity: this.props.ticketQuantity,
        assortment: this.props.assortment,
        passOnFees: this.props.passOnFees,
        eventTotal: this.props.eventTotal,
        attendeeTotal: this.props.attendeeTotal,
        currencySymbol: this.props.currencySymbol,
        isLoading: this.props.isLoading,
        isFree: this.props.isFree,
        defaultCurrency: this.props.defaultCurrency,
        currencyFormat: this.props.currencyFormat,
        impossiblePriceEncountered: false,
    };

    state = this.initialState;

    calculateFees = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        const data = formatDataForAPI(this.state);

        const { valid, errorMessages } = validate(this.state);

        if (valid) {
            this._clearErrorMessages();
            if (this.isEventFree()) {
                this.setState({
                    isFree: true,
                    isLoading: false,
                });
            } else {
                this.setState(
                    {
                        isFree: false,
                        isLoading: true,
                    },
                    () => {
                        pricingCalculatorApi(data)
                            .then(this.setResponseToState)
                            .finally(() =>
                                this.setState({
                                    isLoading: false,
                                }),
                            );
                    },
                );
            }
        } else {
            this._setErrorMessages(errorMessages);
        }
    };

    setResponseToState = (response) => {
        const formattedResponse = formatAPIResponse(response);

        this.setState(formattedResponse);
    };

    isEventFree = () => {
        const { ticketPrice } = this.state;

        if (ticketPrice) {
            return parseFloat(ticketPrice) <= 0;
        }

        return true;
    };

    handleInputChange = (event = {}, cb) => {
        const target = event.target;
        const value =
            target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;

        if (WHITELISTED_EVENT_PARAMETERS.includes(name)) {
            this.setState(
                {
                    [name]: value,
                },
                () => {
                    if (cb && typeof cb === 'function') {
                        cb(this.state);
                    }
                },
            );
        }
    };

    _isCostFree = (cost) => !cost || cost === 0 || /^[0.,]+$/.test(cost);

    _cleanTicketAmountInput = (ticketPrice) => {
        const currency = this.state.defaultCurrency;
        const currencyFormat = this.state.currencyFormat;
        const currencyPrecision = getCurrencyPrecision(currency);
        let sanitizedPrice =
            ticketPrice &&
            formatNumber(
                unformat(ticketPrice, currencyFormat),
                currencyFormat,
                { precision: currencyPrecision },
            );

        if (this._isCostFree(sanitizedPrice)) {
            sanitizedPrice = '';
        }

        return sanitizedPrice;
    };

    _setErrorMessages = (errorMessages) => {
        const allowedErrorMessages = pick(errorMessages, ERROR_MESSAGE_PROPS);

        this.setState(allowedErrorMessages);
    };

    _clearErrorMessages = () => {
        const resetErrorState = ERROR_MESSAGE_PROPS.reduce((acc, key) => {
            const newErrorState = { ...acc };

            newErrorState[key] = '';
            return newErrorState;
        }, {});

        this.setState(resetErrorState);
    };

    updateTicketPriceState = (ticketPrice, cb) => {
        const cleanedTicketPrice = this._cleanTicketAmountInput(ticketPrice);

        this.updateEventState({ ticketPrice: cleanedTicketPrice }, cb);
    };

    updateEventState = (eventState, cb) => {
        const cleanedEventState = pick(
            eventState,
            WHITELISTED_EVENT_PARAMETERS,
        );

        this.setState(cleanedEventState, () => {
            if (cb && typeof cb === 'function') {
                cb(this.state);
            }
        });
    };

    getInputProps = (name, type = 'text', onChange) => ({
        name,
        id: name,
        value: this.state[name],
        type,
        onChange: onChange || this.handleInputChange,
    });

    getStateAndHelpers() {
        const ticketPriceProps = this.getInputProps('ticketPrice');
        const ticketQuantityProps = this.getInputProps('ticketQuantity');

        return {
            ticketPriceProps,
            ticketQuantityProps,
            handleInputChange: this.handleInputChange,
            updateEventState: this.updateEventState,
            calculateFees: this.calculateFees,
            updateTicketPriceState: this.updateTicketPriceState,
            ...this.state,
        };
    }

    render() {
        return this.props.children(this.getStateAndHelpers());
    }
}

export default PricingCalculator;
